import React, { useState } from "react";
import { toast } from "sonner";

const SITE = "https://makeupbrushbasics.com";

/**
 * ShareResultBlock — appears under the quiz result.
 * Lets the user copy a shareable link, tweet, pin, or text their quiz outcome.
 */
export default function ShareResultBlock({ result, brushMap }) {
  const [copied, setCopied] = useState(false);

  if (!result || !result.recommended_ids?.length) return null;

  const topBrush = brushMap[result.recommended_ids[0]];
  const topName = topBrush?.name || "the right brush";
  const count = result.recommended_ids.length;

  // Compact share URL — encodes the recommended IDs so the recipient lands on a pre-loaded result.
  const shareUrl = `${SITE}/quiz?shared=${encodeURIComponent(result.recommended_ids.join(","))}`;
  const shareText = `My BrushBasics edit: ${count} brushes built for me, starting with the ${topName}. Take the 60-second quiz and find yours →`;

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const pinUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(`${SITE}/images/og-default.jpg`)}&description=${encodeURIComponent(shareText)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Long-press the link instead.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({ title: "My BrushBasics edit", text: shareText, url: shareUrl });
    } catch {
      /* user dismissed */
    }
  };

  return (
    <div
      data-testid="quiz-share-block"
      className="mt-12 lg:mt-16 bg-white border border-[#E5E0D8] p-8 lg:p-10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          <div className="overline mb-3">Share your edit</div>
          <h3 className="font-serif-display text-3xl sm:text-4xl text-stone-900 font-light leading-tight">
            Tell a friend who&apos;s
            <br />
            <em className="italic text-stone-700">still guessing.</em>
          </h3>
          <p className="mt-4 text-sm text-stone-600 leading-relaxed max-w-md">
            Send them your result — they&apos;ll see your top {count} brushes and can take the quiz themselves. No paywall, no signup required.
          </p>
        </div>

        <div className="lg:col-span-5 space-y-3">
          <button
            data-testid="share-native"
            onClick={nativeShare}
            className="w-full text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-6 py-3.5 transition-colors"
          >
            Share My Edit
          </button>

          <div className="grid grid-cols-3 gap-2">
            <a
              data-testid="share-twitter"
              href={tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-[0.18em] text-center border border-[#E5E0D8] hover:border-[#8C6A5D] hover:text-[#8C6A5D] text-stone-700 px-3 py-3 transition-colors"
            >
              Tweet
            </a>
            <a
              data-testid="share-pinterest"
              href={pinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-[0.18em] text-center border border-[#E5E0D8] hover:border-[#8C6A5D] hover:text-[#8C6A5D] text-stone-700 px-3 py-3 transition-colors"
            >
              Pinterest
            </a>
            <a
              data-testid="share-facebook"
              href={fbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-[0.18em] text-center border border-[#E5E0D8] hover:border-[#8C6A5D] hover:text-[#8C6A5D] text-stone-700 px-3 py-3 transition-colors"
            >
              Facebook
            </a>
          </div>

          <button
            data-testid="share-copy"
            onClick={copyLink}
            className="w-full text-[10px] uppercase tracking-[0.18em] text-stone-600 hover:text-[#8C6A5D] px-3 py-2.5 transition-colors flex items-center justify-center gap-2"
          >
            {copied ? "✓ Link copied" : "Copy link"}
          </button>
        </div>
      </div>
    </div>
  );
}
